import type { ReactNode } from "react";
import { cva } from "class-variance-authority";
import { CircleAlert, FlaskConical, Info, Sparkles, TriangleAlert, X, type LucideIcon } from "lucide-react";
import { cn } from "../../lib/cn";
import { useKitCopy } from "../../lib/kit-copy";

type Tone = "info" | "warn" | "critical" | "trial" | "success";
const bannerTones = cva("flex items-start gap-3 rounded-card p-4 text-sm", {
  variants: {
    tone: {
      info: "bg-low-bg text-text",
      warn: "bg-medium-bg text-text",
      critical: "bg-exploited-bg text-text",
      trial: "bg-brand-50 text-text",
      success: "bg-ok-bg text-text",
    },
  },
});
const iconTone: Record<Tone, [LucideIcon, string]> = {
  info: [Info, "text-low"],
  warn: [TriangleAlert, "text-medium"],
  critical: [CircleAlert, "text-exploited"],
  trial: [Sparkles, "text-brand-600"],
  success: [Info, "text-ok"],
};

/**
 * Banner (05 §4): info / warn / critical / trial. Plain language: what it
 * means + what to do next (05 §5). Critical banners are announced (role=alert).
 */
export function Banner({ tone, title, children, action, onDismiss }: { tone: Tone; title: ReactNode; children?: ReactNode; action?: ReactNode; onDismiss?: () => void }) {
  const [Icon, color] = iconTone[tone];
  const l = useKitCopy().labels;
  return (
    <div role={tone === "critical" ? "alert" : "status"} className={bannerTones({ tone })}>
      <Icon aria-hidden className={cn("mt-0.5 size-5 shrink-0", color)} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-semibold">{title}</p>
        {children && <div className="text-text-muted">{children}</div>}
        {action && <div className="pt-1">{action}</div>}
      </div>
      {onDismiss && (
        <button type="button" onClick={onDismiss} aria-label={l.dismiss} className="-m-2 inline-grid size-touch place-items-center rounded-btn text-text-muted hover:bg-surface/60 lg:size-8">
          <X aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}

/** Inline alert next to a field or section. Errors always carry a reason and a fix. */
export function InlineAlert({ tone, children }: { tone: "info" | "warn" | "error"; children: ReactNode }) {
  const Icon = tone === "error" ? CircleAlert : tone === "warn" ? TriangleAlert : Info;
  return (
    <p role={tone === "error" ? "alert" : undefined} className={cn("flex items-start gap-2 text-xs", tone === "error" && "text-exploited", tone === "warn" && "text-medium", tone === "info" && "text-low")}>
      <Icon aria-hidden className="mt-px size-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}

/**
 * Test-workspace stripe (04 §12): orange, striped, always on top of the app.
 * Dark text on the stripe (white would be 3.2:1). A labelled region, so it is a
 * landmark screen-reader users can find.
 */
export function TestWorkspaceStripe({ children }: { children?: ReactNode }) {
  const l = useKitCopy().labels;
  return (
    <div role="region" aria-label={l.testWorkspace} className="test-stripe flex h-8 items-center justify-center gap-2 text-2xs font-semibold uppercase tracking-wide">
      <FlaskConical aria-hidden className="size-4" />
      <span className="rounded-xs bg-test px-1.5">{children ?? l.testWorkspaceStripe}</span>
    </div>
  );
}
